/**
 * The POS master data half of the Effort Matrix screen (R4.2, R4.3, R9.4).
 *
 * Two sources, deliberately separate:
 *
 * - The POS_Segment records themselves come from the shared selectable master-data
 *   read (`useManpowerReferenceSet('pos-segment')`), which already carries each
 *   segment's OneData-sourced count and the timestamp it was sourced at. There is no
 *   second endpoint for them.
 * - The active overrides come from `GET /api/manpower/population-overrides`, because
 *   an override lives on an Initiative's Field Force scope entry rather than on
 *   master data, and reading it has to respect the caller's Initiative read scope
 *   (R7.6) — which a master-data route neither does nor should.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch } from '@/shared/api/client';
import { manpowerQueryKeys } from '../manpower-query-keys';

/**
 * One active Impacted_Population_Count override (R4.3).
 *
 * `derivedCount` is the POS_Segment-derived value the override replaced, retained
 * rather than discarded because R4.3 requires it to be shown *alongside* the
 * override — an override whose original is invisible cannot be reviewed. `null` when
 * the entry carries no segment selection, so there was never a derived value to
 * deviate from.
 */
export interface PopulationOverride {
  readonly driverId: string;
  readonly initiativeId: string;
  readonly initiativeRef: string;
  readonly title: string;
  readonly ffRole: string;
  readonly derivedCount: number | null;
  readonly overrideCount: number;
  readonly reason: string;
  readonly posSegmentCount: number;
}

const responseSchema = z.object({
  items: z
    .array(
      z.object({
        driverId: z.string(),
        initiativeId: z.string(),
        initiativeRef: z.string(),
        title: z.string(),
        ffRole: z.string(),
        derivedCount: z.number().nullable().default(null),
        overrideCount: z.number(),
        reason: z.string(),
        posSegmentCount: z.number().int().min(0).default(0),
      }),
    )
    .default([]),
});

/** Fetches every active population override the caller may read (R4.3, R7.6). */
export async function fetchPopulationOverrides(): Promise<readonly PopulationOverride[]> {
  const body = await apiFetch<unknown>('/api/manpower/population-overrides');
  return responseSchema.parse(body).items;
}

/** React Query hook for {@link fetchPopulationOverrides}. */
export function usePopulationOverrides(options?: {
  readonly enabled?: boolean;
}): UseQueryResult<readonly PopulationOverride[], unknown> {
  return useQuery({
    queryKey: manpowerQueryKeys.populationOverrides(),
    queryFn: fetchPopulationOverrides,
    enabled: options?.enabled ?? true,
  });
}
